import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import { Checkbox } from '../atoms';
import type { Genre } from '../../types/api/genre';
import { GenreType } from '../../types/api/genre';

export interface GenreFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    payload: {
      name: string;
      nameMm: string;
      description: string;
      colorCode: string;
      isActive: boolean;
      sortOrder: number;
      genreType: GenreType;
    };
    iconFile: File | null;
  }) => void | Promise<void>;
  mode: 'create' | 'edit';
  genre?: Genre;
}

type GenreFormState = {
  name: string;
  nameMm: string;
  description: string;
  colorCode: string;
  isActive: boolean;
  sortOrder: number;
  genreType: GenreType;
};

const createDefaultState = (): GenreFormState => ({
  name: '',
  nameMm: '',
  description: '',
  colorCode: '#EAB308',
  isActive: true,
  sortOrder: 1,
  genreType: GenreType.CONTENT,
});

export const GenreFormModal: React.FC<GenreFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  genre,
}) => {
  const [formData, setFormData] = useState<GenreFormState>(createDefaultState());
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (genre && mode === 'edit') {
      setFormData({
        name: genre.name || '',
        nameMm: genre.nameMm || '',
        description: genre.description || '',
        colorCode: genre.colorCode || '#EAB308',
        isActive: genre.isActive ?? true,
        sortOrder: genre.sortOrder || 1,
        genreType: genre.genreType ?? GenreType.CONTENT,
      });
      setIconPreview(genre.iconUrl || null);
      setIconFile(null);
    } else {
      setFormData(createDefaultState());
      setIconFile(null);
      setIconPreview(null);
    }
    setErrors({});
  }, [genre, mode, isOpen]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (mode === 'create') {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.nameMm.trim()) newErrors.nameMm = 'Name (Myanmar) is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        payload: formData,
        iconFile,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.match('image/(jpeg|png|webp)')) {
      setErrors((prev) => ({ ...prev, iconFile: 'Allowed formats: JPEG, PNG, WebP' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, iconFile: 'Max size is 5MB' }));
      return;
    }

    setIconFile(file);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.iconFile;
      return newErrors;
    });

    const reader = new FileReader();
    reader.onloadend = () => {
      setIconPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    input.value = '';
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.iconFile;
      return newErrors;
    });
    if (mode === 'create') {
      setIconPreview(null);
    } else {
      setIconPreview(genre?.iconUrl || null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Add New Genre' : 'Edit Genre'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isSubmitting} type="submit">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            required
            placeholder="e.g., Action"
          />
          <FormField
            label="Name (Myanmar)"
            value={formData.nameMm}
            onChange={(e) => handleChange('nameMm', e.target.value)}
            error={errors.nameMm}
            required
            placeholder="e.g., အက်ရှင်"
          />
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Genre description"
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
          />
          {errors.description && (
            <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">Genre Type</label>
            <select
              value={formData.genreType}
              onChange={(e) => handleChange('genreType', e.target.value as GenreType)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            >
              <option value={GenreType.CONTENT}>Content (Movies/Dramas/Documentaries)</option>
              <option value={GenreType.MUSIC}>Music (Music Videos)</option>
            </select>
          </div>
          <FormField
            label="Color Code"
            type="color"
            value={formData.colorCode}
            onChange={(e) => handleChange('colorCode', e.target.value)}
            error={errors.colorCode}
            helperText="Color for genre display"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-left text-gray-700">Genre Icon</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#EAB308]/10 file:text-[#EAB308] hover:file:bg-[#EAB308]/20"
          />
          <p className="text-xs text-gray-500">Max size 5MB. Accepted formats: JPEG, PNG, WebP.</p>
          {errors.iconFile && <p className="text-sm text-red-600">{errors.iconFile}</p>}
          {iconPreview && (
            <div className="flex items-center gap-4">
              <img src={iconPreview} alt="Icon preview" className="w-20 h-20 rounded-lg object-cover border" />
              <Button type="button" variant="outline" size="sm" onClick={handleRemoveIcon}>
                Remove
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Sort Order"
            type="number"
            value={formData.sortOrder.toString()}
            onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 1)}
            error={errors.sortOrder}
            min={1}
            helperText="Display order"
          />
        </div>

        <div className="flex flex-col gap-3">
          <Checkbox
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            label="Active"
            helperText="Genre will be available"
          />
        </div>
      </form>
    </Modal>
  );
};
